import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Animated number that ticks up/down when the value changes.
export function CountUp({ value, className }) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const from = prev.current, to = value;
    prev.current = value;
    if (from === to) { setDisplay(to); return; }
    const start = performance.now(), dur = 650;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className}>{display.toLocaleString()}</span>;
}

// Radial particle burst triggered on a changing nonce.
export function VoteBurst({ trigger, color = "#7B2CBF" }) {
  const [key, setKey] = useState(0);
  useEffect(() => { if (trigger > 0) setKey((k) => k + 1); }, [trigger]);
  if (!key) return null;
  const parts = Array.from({ length: 16 });
  return (
    <div key={key} className="pointer-events-none absolute inset-0 flex items-center justify-center z-30" data-testid="vote-burst">
      {parts.map((_, i) => {
        const a = (i / parts.length) * Math.PI * 2;
        const d = 54 + Math.random() * 46;
        return (
          <motion.span key={i} initial={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            animate={{ opacity: 0, x: Math.cos(a) * d, y: Math.sin(a) * d, scale: 0.2 }}
            transition={{ duration: 0.75, ease: "easeOut" }}
            className="absolute w-2 h-2 rounded-full" style={{ background: color }} />
        );
      })}
    </div>
  );
}

// Floating "+1" that rises and fades, keyed on nonce.
export function FloatingPlusOne({ trigger, color = "#7B2CBF" }) {
  const [key, setKey] = useState(0);
  useEffect(() => { if (trigger > 0) setKey((k) => k + 1); }, [trigger]);
  return (
    <AnimatePresence>
      {key > 0 && (
        <motion.span key={key} initial={{ opacity: 0, y: 6, scale: 0.6 }}
          animate={{ opacity: 1, y: -34, scale: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-2 font-heading font-extrabold text-lg z-30"
          style={{ color }} data-testid="floating-plus-one">+1</motion.span>
      )}
    </AnimatePresence>
  );
}

export function haptic(ms = 25) {
  try { if (navigator.vibrate) navigator.vibrate(ms); } catch {}
}
